/**
 * Real API Metrics Service — calls backend via /api/monitoring/*
 */
import type { SystemHealth, SystemMetrics, DashboardMetrics, ProcessingTrend } from '../../../types/starnexus';
import { apiUrl, apiFetch } from '../../../utils/apiClient';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }
  return res.json();
}

export const realMetricsService = {
  async getSystemHealth(): Promise<SystemHealth> {
    return request('/monitoring/health');
  },

  async getSystemMetrics(): Promise<SystemMetrics> {
    return request('/monitoring/metrics');
  },

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return request('/monitoring/metrics/dashboard');
  },

  async getProcessingTrends(days: number = 7): Promise<ProcessingTrend[]> {
    return request(`/monitoring/metrics/trends?days=${days}`);
  },

  async getDatasetVolumes(limit: number = 5): Promise<Array<{ name: string; volume: number }>> {
    return request(`/monitoring/metrics/volumes?limit=${limit}`);
  },

  async getErrorRateTrends(days: number = 30): Promise<Array<{ date: string; errorRate: number }>> {
    return request(`/monitoring/errors/trends?days=${days}`);
  },

  async getRecentErrors(limit: number = 10): Promise<Array<{ id: string; message: string; timestamp: string; severity: string }>> {
    return request(`/monitoring/errors?limit=${limit}`);
  },

  async getDatasetMetrics(datasetId: string): Promise<{
    filesProcessed: number;
    successRate: number;
    avgProcessingTime: number;
    trends: ProcessingTrend[];
  }> {
    return request(`/monitoring/metrics/dataset/${datasetId}`);
  },

  reset(): void { /* no-op for real service */ },
};
